/**
 * Переключатель брендов — знак в шапке (docs/multi-brand.md §6).
 *
 * ПОЧЕМУ НА ЗНАКЕ, А НЕ ВКЛАДКАМИ. Бренд — это канал, тема и данные всего рабочего места, а не
 * ещё один экран рядом с «Бэклогом»: переключение меняет всё под шапкой разом. Знак и есть
 * «чей это интерфейс», поэтому нажатие на него и спрашивает «чей открыть». Место в шапке он
 * не прибавляет — лестница подписей (`Shell.module.css`) мерена с ним.
 *
 * ЧТО ПОКАЗЫВАЕТ. Каждый бренд — своим знаком на плашке СВОЕЙ темы (`data-theme` на плашке:
 * GeoBoost — на светлой, AGI Boardroom — на чёрной) и названием; открытый отмечен. Плашка —
 * это вид, в который человек перейдёт, до перехода. С одним брендом меню тоже открывается:
 * «другого нет» — ответ, а не мёртвая кнопка.
 *
 * КУДА ВЕДЁТ. Вкладка сохраняется (бэклог, контент-план, мониторинг); черновик, стиль,
 * сравнение и пакет ведут в бэклог: их id чужому бренду не принадлежат. Пункты — настоящие
 * ссылки (`<Link>`), а не программный переход: несохранённую правку редактора стилей сторожит
 * перехват щелчков по ссылкам (`StylesPage.tsx`), и смена бренда обязана пройти через тот же
 * вопрос. Открытый бренд ведёт на текущий адрес: тот же сторож его не спрашивает, а меню
 * просто закрывается.
 *
 * ГЕОМЕТРИЯ И ЗАКРЫТИЕ — как у `InfoPopover.tsx`: `position: fixed` от координат кнопки, место
 * считается до отрисовки кадра; щелчок мимо, прокрутка, смена размера окна и Escape закрывают.
 * Escape слушается на погружении и дальше не идёт, фокус возвращается на кнопку.
 *
 * КЛАВИАТУРА — шаблон WAI-ARIA «menu button»: Enter/Space открывают и ставят фокус на открытый
 * бренд, ↓/↑ на закрытой кнопке открывают на первом/последнем, внутри — ↓/↑ по кругу,
 * Home/End, Enter/Space выбирают, Tab закрывает и уводит фокус дальше.
 */

import {
  type JSX,
  type KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'wouter';
import { brandLook, useBrands } from '../brand.tsx';
import { BrandMark } from './BrandMark.tsx';
import styles from './BrandSwitcher.module.css';
import { IconCheck, IconChevronDown } from './icons.tsx';
import type { TabId } from './Shell.tsx';

/** Ширина меню. */
const WIDTH = 280;
/** Поле от края окна. */
const EDGE = 8;
/** Зазор между кнопкой и меню. */
const GAP = 6;

const popWidth = (): number => Math.max(0, Math.min(WIDTH, window.innerWidth - 2 * EDGE));

/** Вкладки, которые переход между брендами сохраняет: у них нет id внутри бренда. */
const KEPT_TABS: readonly TabId[] = ['backlog', 'plan', 'monitoring'];

/** Вкладка в адресе `/b/:brand/<вкладка>…`; всё прочее — бэклог. */
export function keptTabOf(path: string): TabId {
  const seg = /^\/b\/[^/]+\/([^/?#]+)/.exec(path)?.[1];
  return KEPT_TABS.find((t) => t === seg) ?? 'backlog';
}

interface Place {
  readonly top: number;
  readonly left: number;
}

type FocusOnOpen = 'current' | 'first' | 'last';

/** Пункты меню в порядке показа. */
const itemsIn = (pop: HTMLElement | null): HTMLElement[] =>
  Array.from(pop?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? []);

export function BrandSwitcher({
  brand,
  textClassName,
}: {
  brand: string;
  /** Класс текстовой части знака — его гасит лестница подписей шапки. */
  textClassName?: string | undefined;
}): JSX.Element {
  const state = useBrands();
  const look = brandLook(state, brand);
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Place | null>(null);
  const menuId = useId();
  const headId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const focusOnOpen = useRef<FocusOnOpen>('current');

  const tab = keptTabOf(location);
  const list = state.list ?? [];

  const close = (refocus: boolean): void => {
    setOpen(false);
    if (refocus) btnRef.current?.focus();
  };

  const openWith = (focus: FocusOnOpen): void => {
    focusOnOpen.current = focus;
    setOpen(true);
  };

  // Место — до отрисовки кадра, по уже разложенному, но ещё невидимому меню (как у InfoPopover).
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const btn = btnRef.current;
    if (btn === null) return;
    const r = btn.getBoundingClientRect();
    const width = popWidth();
    const left = Math.min(Math.max(EDGE, r.left), Math.max(EDGE, window.innerWidth - width - EDGE));
    setPlace({ top: r.bottom + GAP, left });
  }, [open]);

  // Фокус — когда меню уже видно (невидимому узлу фокус не передаётся) и в нём есть пункты.
  // Один раз за открытие: список, приехавший после нажатия, получает фокус, когда появится, а
  // перечитанный список не уводит фокус у человека, который уже ходит стрелками.
  const focused = useRef(false);
  const count = list.length;
  useEffect(() => {
    if (!open) {
      focused.current = false;
      return;
    }
    if (place === null || count === 0 || focused.current) return;
    const all = itemsIn(popRef.current);
    const current = all.find((el) => el.getAttribute('aria-checked') === 'true');
    const target =
      focusOnOpen.current === 'last'
        ? all[all.length - 1]
        : focusOnOpen.current === 'first'
          ? all[0]
          : (current ?? all[0]);
    if (target === undefined) return;
    target.focus();
    focused.current = true;
  }, [open, place, count]);

  // Адрес сменился не через меню («Назад», ссылка под ним) — меню про прежний адрес закрывается.
  const seenLocation = useRef(location);
  useEffect(() => {
    if (seenLocation.current === location) return;
    seenLocation.current = location;
    setOpen(false);
  }, [location]);

  // Щелчок мимо, прокрутка страницы, смена размера окна, Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const wrap = wrapRef.current;
      if (wrap !== null && e.target instanceof Node && wrap.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = (e: Event): void => {
      // Прокрутка внутри самого меню (длинный список брендов) его не закрывает.
      if (e.target instanceof Node && popRef.current?.contains(e.target) === true) return;
      setOpen(false);
    };
    const onResize = (): void => {
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      btnRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const onButtonKey = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openWith(e.key === 'ArrowDown' ? 'first' : 'last');
    }
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    const all = itemsIn(popRef.current);
    const focused = document.activeElement;
    const at = focused instanceof HTMLElement ? all.indexOf(focused) : -1;
    const move = (to: number): void => {
      e.preventDefault();
      all[(to + all.length) % all.length]?.focus();
    };
    switch (e.key) {
      case 'ArrowDown':
        move(at + 1);
        break;
      case 'ArrowUp':
        move(at < 0 ? all.length - 1 : at - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(all.length - 1);
        break;
      case ' ':
        // У ссылки Space не нажимает её сама — нажимаем, как Enter.
        if (at >= 0) {
          e.preventDefault();
          all[at]?.click();
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapRef} className={styles['wrap']}>
      <button
        ref={btnRef}
        type="button"
        className={open ? `brand ${styles['btn']} ${styles['btnOn']}` : `brand ${styles['btn']}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Сменить бренд — открыт ${look.title}`}
        title="Сменить бренд"
        onClick={() => {
          if (open) close(false);
          else openWith('current');
        }}
        onKeyDown={onButtonKey}
      >
        <BrandMark look={look} textClassName={textClassName} />
        <IconChevronDown className={styles['chev']} width={12} height={12} />
      </button>

      {open ? (
        <div
          ref={popRef}
          className={styles['pop']}
          style={
            place === null
              ? { visibility: 'hidden', top: 0, left: 0, width: popWidth() }
              : { top: place.top, left: place.left, width: popWidth() }
          }
        >
          <div className={styles['head']} id={headId}>
            Бренды
          </div>
          {state.list === undefined ? (
            <div className={styles['status']}>
              {state.error === null
                ? 'Список брендов загружается…'
                : `Список брендов не загрузился: ${state.error}`}
            </div>
          ) : null}
          {list.length > 0 ? (
            <div
              id={menuId}
              role="menu"
              aria-labelledby={headId}
              tabIndex={-1}
              className={styles['menu']}
              onKeyDown={onMenuKey}
            >
              {list.map((b) => {
                const current = b.id === brand;
                return (
                  <Link
                    key={b.id}
                    href={current ? location : `/b/${b.id}/${tab}`}
                    role="menuitemradio"
                    aria-checked={current}
                    aria-label={b.title}
                    tabIndex={-1}
                    className={styles['item']}
                    onClick={(e) => {
                      if (current) e.preventDefault();
                      close(true);
                    }}
                  >
                    <span className={styles['tile']} data-theme={b.look.theme} aria-hidden="true">
                      <BrandMark look={b.look} size="menu" />
                    </span>
                    <span className={styles['cap']} aria-hidden="true">
                      <span className={styles['title']}>{b.title}</span>
                      {current ? (
                        <span className={styles['cur']}>
                          <IconCheck width={14} height={14} />
                          открыт
                        </span>
                      ) : null}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : state.list !== undefined ? (
            <div className={styles['status']}>Бэкенд не отдал ни одного бренда.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
